import { useAuth } from '@contexts/User.context';

import AdminView from '@components/layout/views/AdminView';
import Alert from '@components/alert/Alert';
import StripeProducts from '@components/products/StripeProducts';
import ProductsFooter from '@components/products/ProductsFooter';
import BillingPortal from '@components/subscription/BillingPortal';

import { humanFileSize } from '@utils/files';
import { formatPeriodDate, getCancellation, getPlanLimitLines } from '@utils/subscriptionState';

/**
 * Subscription page (`/subscription`).
 *
 * Shows the user's own subscription, driven only by `subscription_state` on the
 * v2 profile via `subscriptionView` (see src/utils/subscriptionState.js):
 * trial info, plan name, renewal or end date, plan limits, storage, Manage billing and
 * the plan list, depending on the state. Premium, organization and admin users
 * only get a short note.
 *
 * Display only: payment gating is the backend's job (HTTP 402, issue #603).
 */
const Subscription = () => {
	const { currentUser, subscriptionView } = useAuth();
	const state = currentUser?.subscription_state ?? null;
	const { view, daysLeft } = subscriptionView ?? {};
	// Worked out once, so the canceled line and the Manage billing note always agree
	const cancellation = view === 'active' ? getCancellation(state) : null;
	const billing = getBillingPortalProps(view, cancellation);

	// No load-error branch: when the profile fails to load, the user context falls back
	// to the logged-out user and RequireAuth sends them to /login before this renders.
	return (
		<AdminView title="Subscription">
			<SubscriptionContent
				view={view}
				daysLeft={daysLeft}
				state={state}
				cancellation={cancellation}
				currentUser={currentUser}
			/>
			{/* One instance for every view, after the view's content: a view change during a
			    portal request (e.g. pending → active on a profile refetch) keeps it mounted,
			    so the click still opens the portal */}
			<BillingPortal show={billing.show} note={billing.note} />
		</AdminView>
	);
};
export default Subscription;

/**
 * Whether a view shows Manage billing, and the note after the button. The button
 * itself only renders for users with a Stripe customer.
 *
 * @param {string} view `subscriptionView.view`
 * @param {{ endsAt: number|null }|null} cancellation `getCancellation(state)`, active view only
 * @returns {{ show: boolean, note?: string }}
 */
const getBillingPortalProps = (view, cancellation) => {
	switch (view) {
		case 'active':
			return {
				show: true,
				note: cancellation
					? 'Resume or change your plan in the Stripe billing portal.'
					: 'Change plan, update your payment method, or cancel in the Stripe billing portal.',
			};
		case 'pending':
			return {
				show: true,
				note: 'Finish or cancel the payment in the Stripe billing portal.',
			};
		case 'past_due':
		case 'unknown':
			return { show: true };
		default:
			return { show: false };
	}
};

const SubscriptionContent = ({ view, daysLeft, state, cancellation, currentUser }) => {
	const endDate = formatPeriodDate(state?.current_period_end);

	switch (view) {
		case 'hidden':
			if (currentUser?.active_org_id) {
				return (
					<p>
						Your access is provided by{' '}
						{currentUser.active_org_name || 'your organization'}. Billing is handled by
						your organization.
					</p>
				);
			}
			return <p>Your account has full access. There is no subscription to manage.</p>;

		case 'trial_active':
			return (
				<>
					<h2>You're on a free trial</h2>
					<p>
						{daysLeft === 0 && 'Your trial ends today. '}
						{endDate && daysLeft > 0 && (
							<>
								Your trial ends on {endDate} ({daysLeft}{' '}
								{daysLeft === 1 ? 'day' : 'days'} left).{' '}
							</>
						)}
						Choose a plan to keep using the portal after that.
					</p>
					<Plans />
				</>
			);

		case 'trial_ended':
			return (
				<>
					<h2>Your free trial has ended</h2>
					<p>Choose a plan to keep using the portal.</p>
					<Plans />
				</>
			);

		case 'active':
			return (
				<PlanDetails
					state={state}
					cancellation={cancellation}
					storageUsage={currentUser?.storage_usage}
				/>
			);

		case 'past_due':
			return (
				<>
					<Alert variant="warning">
						Your last payment failed. Update your payment method to keep your
						subscription.
					</Alert>
					{/* No renews/cancels line: it would contradict the payment-failed alert */}
					<PlanDetails
						state={state}
						storageUsage={currentUser?.storage_usage}
						showPeriod={false}
					/>
				</>
			);

		case 'pending':
			// No plans: the user may already have paid, and a second checkout would start
			// a second subscription
			return (
				<>
					<Alert variant="warning">
						Your payment hasn't been completed, so your subscription hasn't started yet.
					</Alert>
					{/* Without a Stripe customer there is no Manage billing after this */}
					{!currentUser?.stripe_customer_id && (
						<p className="mt-4">
							If you need help finishing or canceling the payment, contact{' '}
							<a href="mailto:post@biodrone.no">post@biodrone.no</a>.
						</p>
					)}
				</>
			);

		case 'inactive': {
			const plan = state?.plan_name
				? `Your ${state.plan_name} subscription`
				: 'Your subscription';
			return (
				<>
					<h2>Your subscription has ended</h2>
					<p>
						{plan} ended{endDate ? ` on ${endDate}` : ''}. Choose a plan to subscribe
						again.
					</p>
					<Plans />
				</>
			);
		}

		case 'never_started': {
			const plan = state?.plan_name
				? `Your ${state.plan_name} subscription`
				: 'Your subscription';
			return (
				<>
					<h2>Your payment wasn't completed</h2>
					<p>
						{plan} never started because the payment wasn't completed. Choose a plan to
						try again.
					</p>
					<Plans />
				</>
			);
		}

		case 'unknown':
			return (
				<>
					<p>
						We couldn't determine the status of your subscription (
						{state?.status ?? 'unknown'}). Check it in the billing portal, or contact{' '}
						<a href="mailto:post@biodrone.no">post@biodrone.no</a>.
					</p>
				</>
			);

		default:
			// 'none': no subscription state at all
			return (
				<>
					<h2>Choose a plan</h2>
					<Plans />
				</>
			);
	}
};

/**
 * Plan name, renewal date (or end date once canceled), plan limits and storage use
 * for a running subscription. `cancellation` is `getCancellation(state)`.
 * `showPeriod={false}` drops the renews/cancels line.
 */
const PlanDetails = ({ state, storageUsage, cancellation = null, showPeriod = true }) => {
	const endDate = formatPeriodDate(state?.current_period_end);
	const cancelDate = formatPeriodDate(cancellation?.endsAt);
	const limitLines = getPlanLimitLines(state);

	return (
		<>
			{state?.plan_name && <h2>{state.plan_name}</h2>}
			{showPeriod &&
				(cancellation ? (
					<p>
						{cancelDate
							? `Your subscription is canceled and ends on ${cancelDate}.`
							: 'Your subscription is canceled and will end at the end of the current period.'}{' '}
						You keep access until then.
					</p>
				) : (
					endDate && <p>Your subscription renews on {endDate}.</p>
				))}
			{limitLines.length > 0 && (
				<>
					<p className="mb-1">
						<strong>Included in your plan:</strong>
					</p>
					<ul>
						{limitLines.map(line => (
							<li key={line}>{line}</li>
						))}
					</ul>
				</>
			)}
			{state?.storage_limit != null && (
				<p>
					<strong>Storage:</strong> {humanFileSize(storageUsage ?? 0)} of{' '}
					{humanFileSize(state.storage_limit)} used
				</p>
			)}
		</>
	);
};

/** The plan tiles, shown in every state where the user can (re)subscribe. */
const Plans = () => (
	<section aria-labelledby="plans-heading" className="mt-5">
		<h2 id="plans-heading">Plans</h2>
		<StripeProducts />
		<ProductsFooter />
	</section>
);
